import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Chip,
  Select,
  SelectItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useTabParam,
} from '@structural-climbing/ui'
import { InvoiceModal } from './InvoiceModal'
import type { OrderRow } from './types'
import { canDeleteOrder, paymentMethodBadgeClass } from './paymentMethod'
import { stackOnPhone } from '../../components/stackOnPhone'
import { ListPages } from '../../components/ListPages'
import { GymDialog } from '../../components/GymDialog'

const DATE_FILTERS = ['all', 'today', 'week', 'month'] as const
type DateFilter = (typeof DATE_FILTERS)[number]
const PAYMENT_FILTERS = ['all', 'cash', 'card', 'online', 'transfer'] as const
type PaymentFilter = (typeof PAYMENT_FILTERS)[number]

const PAGE_SIZE = 20

interface InvoicesResponse {
  invoices: OrderRow[]
  total: number
  total_amount: number
  has_more: boolean
}

// The server filters on created_at, so each window becomes a from/to pair. The bounds are
// built from browser-local Date parts on purpose: the desk reads "today" as the day it is
// standing in, which is what the old client-side filter did too.
function dateRange(filter: DateFilter): { from?: string; to?: string } {
  if (filter === 'all') return {}
  const now = new Date()
  if (filter === 'today') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
    return { from: start.toISOString(), to: end.toISOString() }
  }
  if (filter === 'week') {
    const weekAgo = new Date(now)
    weekAgo.setDate(weekAgo.getDate() - 7)
    return { from: weekAgo.toISOString() }
  }
  // month: the calendar month we are in, not the last 30 days.
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  return { from: start.toISOString(), to: end.toISOString() }
}

export function InvoicesSection() {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [orders, setOrders] = useState<OrderRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // In the URL so the dashboard's income card can open today's sales, per method.
  const [dateFilter, setDateFilter] = useTabParam('date', DATE_FILTERS, 'all')
  const [paymentFilter, setPaymentFilter] = useTabParam('payment', PAYMENT_FILTERS, 'all')
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fetchPage = useCallback(
    async (date: DateFilter, payment: PaymentFilter, pageNumber: number) => {
      setLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String((pageNumber - 1) * PAGE_SIZE),
        })
        const { from, to } = dateRange(date)
        if (from) params.set('from', from)
        if (to) params.set('to', to)
        if (payment !== 'all') params.set('payment_method', payment)

        const res = await fetch(`${apiUrl}/invoices?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) {
          setError(t('invoices.errorLoad'))
          return
        }
        const data = (await res.json()) as InvoicesResponse
        setOrders(data.invoices)
        setPage(pageNumber)
        setTotal(data.total)
        setTotalAmount(data.total_amount)
        setError(null)
      } catch {
        setError(t('invoices.errorLoad'))
      } finally {
        setLoading(false)
      }
    },
    [apiUrl, getAccessTokenSilently, t],
  )

  // After a delete: the same page again.
  const reload = useCallback(() => {
    void fetchPage(dateFilter, paymentFilter, page)
  }, [fetchPage, dateFilter, paymentFilter, page])

  useEffect(() => {
    void fetchPage(dateFilter, paymentFilter, 1)
  }, [fetchPage, dateFilter, paymentFilter])

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return (
      d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
      ' ' +
      d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
    )
  }

  async function handleDelete(id: string) {
    setDeleting(true)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/invoices/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      setConfirmDeleteId(null)
      reload()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          size="sm"
          className="w-44"
          aria-label={t('invoices.filterAllDates')}
          selectedKeys={[dateFilter]}
          onSelectionChange={(keys) => {
            const val = String(Array.from(keys)[0] ?? 'all') as DateFilter
            setDateFilter(val)
          }}
        >
          <SelectItem key="all">{t('invoices.filterAllDates')}</SelectItem>
          <SelectItem key="today">{t('invoices.filterToday')}</SelectItem>
          <SelectItem key="week">{t('invoices.filterThisWeek')}</SelectItem>
          <SelectItem key="month">{t('invoices.filterThisMonth')}</SelectItem>
        </Select>

        <Select
          size="sm"
          className="w-44"
          aria-label={t('invoices.filterAllPayments')}
          selectedKeys={[paymentFilter]}
          onSelectionChange={(keys) => {
            const val = String(Array.from(keys)[0] ?? 'all') as PaymentFilter
            setPaymentFilter(val)
          }}
        >
          <SelectItem key="all">{t('invoices.filterAllPayments')}</SelectItem>
          <SelectItem key="cash">{t('invoices.filterCash')}</SelectItem>
          <SelectItem key="card">{t('invoices.filterCard')}</SelectItem>
          <SelectItem key="online">{t('invoices.filterOnline')}</SelectItem>
          <SelectItem key="transfer">{t('invoices.filterTransfer')}</SelectItem>
        </Select>

        <span className="ml-auto text-sm text-foreground/50">
          {t('invoices.summary', {
            count: total,
            total: totalAmount.toFixed(2),
          })}
        </span>
      </div>

      {loading && orders.length === 0 ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" color="primary" />
        </div>
      ) : error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : orders.length === 0 ? (
        <p className="py-8 text-center text-sm text-foreground/40">{t('invoices.empty')}</p>
      ) : (
        <Table
          aria-label={t('invoices.title')}
          classNames={stackOnPhone()}
          className={`transition-opacity ${loading ? 'opacity-60' : ''}`}
        >
          <TableHeader>
            <TableColumn>{t('invoices.colDate')}</TableColumn>
            <TableColumn>{t('invoices.colName')}</TableColumn>
            <TableColumn>{t('invoices.colOrderType')}</TableColumn>
            <TableColumn>{t('invoices.colPayment')}</TableColumn>
            <TableColumn>{t('invoices.colTotal')}</TableColumn>
            <TableColumn>{t('invoices.colType')}</TableColumn>
            <TableColumn>{t('invoices.colActions')}</TableColumn>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell className="text-sm">{formatDate(order.created_at)}</TableCell>
                <TableCell className="text-sm max-md:-order-1 max-md:basis-full max-md:font-medium">
                  {order.plan_name || '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    variant="flat"
                    color={order.order_type === 'product' ? 'secondary' : 'default'}
                  >
                    {t(`invoices.orderType.${order.order_type}`, {
                      defaultValue: order.order_type,
                    })}
                  </Chip>
                </TableCell>
                <TableCell>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${paymentMethodBadgeClass(
                      order.payment_method,
                    )}`}
                  >
                    {t(`invoices.${order.payment_method}`)}
                  </span>
                </TableCell>
                <TableCell className="text-sm font-medium">
                  {Number(order.total_amount).toFixed(2)}
                  {/* The sale price is the revenue and what the receipt says.
                      What Stripe will actually pay out is a different number,
                      and this is the only place the gym can see it without
                      opening their Stripe dashboard. */}
                  {order.net_amount != null && (
                    <span className="block text-xs font-normal text-foreground/45">
                      {t('invoices.netAfterFees', {
                        amount: Number(order.net_amount).toFixed(2),
                      })}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      order.has_invoice
                        ? 'bg-success/10 text-success-700'
                        : 'bg-default/10 text-foreground/50'
                    }`}
                  >
                    {order.has_invoice
                      ? t('invoices.typeBadgeInvoice')
                      : t('invoices.typeBadgeReceipt')}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="light" onClick={() => setActiveOrderId(order.id)}>
                      {t('invoices.viewInvoice')}
                    </Button>
                    {/* No delete for online orders: see canDeleteOrder. */}
                    {canDeleteOrder(order.payment_method) && (
                      <Button
                        size="sm"
                        variant="light"
                        color="danger"
                        isIconOnly
                        aria-label={t('invoices.deleteInvoice')}
                        onClick={() => setConfirmDeleteId(order.id)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ListPages
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={(next) => void fetchPage(dateFilter, paymentFilter, next)}
        isLoading={loading}
      />

      <InvoiceModal orderId={activeOrderId} onClose={() => setActiveOrderId(null)} />

      {confirmDeleteId && (
        <GymDialog onClose={() => setConfirmDeleteId(null)} isDismissable={!deleting} size="sm">
          <div className="p-6">
            <h2 className="mb-2 text-base font-semibold">{t('invoices.deleteConfirm')}</h2>
            <p className="mb-6 text-sm text-foreground/60">{t('invoices.deleteConfirmBody')}</p>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="flat"
                isDisabled={deleting}
                onClick={() => setConfirmDeleteId(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                color="danger"
                isLoading={deleting}
                onClick={() => void handleDelete(confirmDeleteId)}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </GymDialog>
      )}
    </>
  )
}
